/**
 * Alpine.js TypeScript plugin for Algolia facets
 *
 * This plugin handles:
 * - Facet selection and filtering
 * - Updating URL parameters
 * - Refreshing query results using Alpine.morph
 */

import Alpine from 'alpinejs';

// Types for the plugin
interface AlgoliaFacetData {
    queryId: string;
    attribute: string;
    values: string[];
    searchKeywords: string;
    isSearching: boolean;
    isLoading: boolean;

    init(): void;

    toggleFacet(value: string, isChecked: boolean): void;

    updateUrl(): void;

    triggerChange(): void;

    triggerGlobalRefresh(): void;

    refreshResults(): void;

    clearAllFacets(queryId?: string): void;

    performSearch(): void;

    clearSearchKeywords(): void;
}

interface FacetChangedEvent extends CustomEvent {
    detail: {
        queryId: string;
        attribute: string;
        values: string[];
    };
}

// Global refresh handler to prevent multiple refreshes
let refreshTimeout: number | null = null;
let isRefreshing: boolean = false;

// The Alpine plugin
const AlgoliaFacetsPlugin = (Alpine: any) => {
    // Initialize store for active facets
    Alpine.store('activeFacets', {
        facets: {},

        // Remove a facet
        removeFacet(queryId: string, attribute: string, value: string) {
            if (!queryId || !attribute || !value) {
                console.error('Missing parameters for removeFacet', { queryId, attribute, value });
                return;
            }

            if (this.facets[queryId] && this.facets[queryId][attribute]) {
                Alpine.store('ui').loading = true;
                // Get the array of values
                const values = this.facets[queryId][attribute];

                // Find the index of the value (exact match)
                const index = values.findIndex(val => val == value);

                if (index !== -1) {
                    // Remove the value at the found index
                    this.facets[queryId][attribute].splice(index, 1);


                    // Clean up empty arrays
                    if (this.facets[queryId][attribute].length === 0) {
                        delete this.facets[queryId][attribute];
                    }

                    // Clean up empty query objects
                    if (Object.keys(this.facets[queryId]).length === 0) {
                        delete this.facets[queryId];
                    }
                }
            }
        },

        // Get all facet values for a query and attribute
        getFacetValues(queryId: string, attribute: string) {
            if (this.facets[queryId] && this.facets[queryId][attribute]) {
                return this.facets[queryId][attribute];
            }
            return [];
        },

        // Clear all facets for a query
        clearFacets(queryId: string) {
            if (queryId) {
                delete this.facets[queryId];
            } else {
                this.facets = {};
            }
        },

        // Initialize facets from URL
        initFromUrl() {
            const url = new URL(window.location.href);
            const params = url.searchParams;

            this.facets = {};

            for (const key of Array.from(params.keys())) {
                if (key.startsWith('facet[')) {
                    const matches = key.match(/facet\[([^\]]+)\]\[([^\]]+)\]/);
                    if (matches && matches.length === 3) {
                        const queryId = matches[1];
                        const attribute = matches[2];
                        const values = params.get(key)?.split(',') || [];

                        if (!this.facets[queryId]) {
                            this.facets[queryId] = {};
                        }

                        this.facets[queryId][attribute] = values;
                        console.log(this.facets[queryId]);
                    }
                }
            }
        }
    });

    // Initialize the store with URL parameters
    Alpine.store('activeFacets').initFromUrl();

    Alpine.data('algoliaFacet', (queryId: string, attribute: string, initialValues: string[] = [], searchKeywords = ''): AlgoliaFacetData => ({
        queryId,
        attribute,
        values: initialValues,
        searchKeywords,
        isSearching: false,
        isLoading: false,

        init() {
            // Initialize facet values from the store
            if (this.queryId && this.attribute) {
                this.values = Alpine.store('activeFacets').getFacetValues(this.queryId, this.attribute);
            }

            // Handle events from other facets
            document.addEventListener('drberg:facet:changed', ((event: FacetChangedEvent) => {
                // Only process if it's a different facet from the same query
                if (event.detail.queryId === this.queryId && event.detail.attribute !== this.attribute) {
                    // Update UI based on the new state
                    // Instead of calling refreshResults directly, use the global handler
                    this.triggerGlobalRefresh();
                }
            }) as EventListener);
        },

        clearSearchKeywords() {
            Alpine.store('ui').loading = true;
            this.searchKeywords = '';

            const url = new URL(window.location.href);
            url.searchParams.delete('search_keywords');

            window.history.pushState({}, '', url.toString());

            this.triggerChange();
            this.triggerGlobalRefresh();
        },

        performSearch() {
            Alpine.store('ui').loading = true;

            const trimmed = this.searchKeywords.trim();
            const url = new URL(window.location.href);

            if (trimmed === '') {
                url.searchParams.delete('search_keywords');
            } else {
                url.searchParams.set('search_keywords', trimmed);
            }

            window.history.pushState({}, '', url.toString());

            this.triggerChange();
            this.triggerGlobalRefresh();
        },

        clearAllFacets(queryId = '') {
            Alpine.store('ui').loading = true;

            // Clear facets from the store
            Alpine.store('activeFacets').clearFacets(queryId);

            // Clear local values if this component matches the queryId
            if (!queryId || this.queryId === queryId) {
                this.values = [];
            }

            // Also clear search keywords
            this.searchKeywords = '';

            const url = new URL(window.location.href);
            const params = url.searchParams;

            const keysToDelete: string[] = [];

            // Add facet parameters to delete
            const facetPrefix = 'facet[' + queryId + ']';
            for (const key of Array.from(params.keys())) {
                if (queryId) {
                    if (key.startsWith(facetPrefix)) {
                        keysToDelete.push(key);
                    }
                } else {
                    if (key.startsWith('facet[')) {
                        keysToDelete.push(key);
                    }
                }
            }

            // Also remove search_keywords parameter
            if (params.has('search_keywords')) {
                keysToDelete.push('search_keywords');
            }

            keysToDelete.forEach(key => {
                params.delete(key);
            });

            window.history.pushState({}, '', url.toString());

            this.triggerChange();
            this.triggerGlobalRefresh();
        },

        toggleFacet(value: string, isChecked: boolean) {
            Alpine.store('ui').loading = true;
            if (isChecked) {
                // Add value if not already present
                if (!this.values.includes(value)) {
                    this.values.push(value);
                }
            } else {
                // Remove value if present
                const index = this.values.indexOf(value);
                if (index !== -1) {
                    this.values.splice(index, 1);
                }
            }

            // Update URL
            this.updateUrl();

            // Trigger event
            this.triggerChange();

            // Refresh results via global handler
            this.triggerGlobalRefresh();
        },

        updateUrl() {
            const url = new URL(window.location.href);
            const params = url.searchParams;
            const paramKey = `facet[${this.queryId}][${this.attribute}]`;

            // Update or remove parameter
            if (this.values.length > 0) {
                    params.set(paramKey, this.values.join(','));
            } else {
                params.delete(paramKey);
            }

            // Update URL without reloading
            window.history.pushState({}, '', url.toString());
        },

        triggerChange() {
            // Dispatch custom event
            const event = new CustomEvent('drberg:facet:changed', {
                detail: {
                    queryId: this.queryId,
                    attribute: this.attribute,
                    values: this.values
                },
                bubbles: true
            });
            document.dispatchEvent(event);
        },

        triggerGlobalRefresh() {
            if (refreshTimeout) {
                clearTimeout(refreshTimeout);
            }

            // Set a small timeout to debounce multiple changes at once
            refreshTimeout = setTimeout(() => {
                if (!isRefreshing) {
                    this.refreshResults();
                }
            }, 50);
        },

        refreshResults() {
            if (isRefreshing) return;
            isRefreshing = true;

            const url = window.location.href;

            fetch(url)
                .then(response => response.text())
                .then(html => {
                    const parser = new DOMParser();
                    const newDoc = parser.parseFromString(html, 'text/html');

                    const currentMain = document.querySelector('.site-main');
                    const newMain = newDoc.querySelector('.site-main');

                    if (currentMain && newMain) {
                        Alpine.morph(currentMain, newMain);
                        Alpine.initTree(currentMain);
                    } else {
                        console.warn('Could not find <main> element to morph.');
                    }

                    Alpine.store('ui').loading = false;
                    if (this.queryId && this.attribute) {
                        this.values = Alpine.store('activeFacets').getFacetValues(this.queryId, this.attribute);
                    }

                    isRefreshing = false;
                })
                .catch(error => {
                    console.error('Error fetching page:', error);
                    isRefreshing = false;
                });
        }
    }));

    Alpine.store('ui', {
        showFiltersOnMobile: false,
        loading: false,
    });
    Alpine.effect(() => {
        if (Alpine.store('ui').showFiltersOnMobile) {
            document.body.classList.add('overflow-hidden');
        } else {
            document.body.classList.remove('overflow-hidden');
        }

        if (Alpine.store('ui').loading) {
            document.body.classList.add('algolia-loading');
        } else {
            document.body.classList.remove('algolia-loading');
        }
    });
};

// Export the plugin
export default AlgoliaFacetsPlugin;
